import { Formik } from 'formik';
import { useState, FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdArrowBackIosNew } from 'react-icons/md';
import { categorySchema } from '../../../../../constants';
import { DAV_APIS } from '../../../../../adapters';
import { Alert, SEO } from '../../../../commons';
import { useCategory, useSearchQuery } from '../../../../../hooks';
import DashboardLoader from '../../../sections/Loader/DashboardLoader';
import CategoryForm from '../CategoryForm';
import { Button } from '../../../../ui/Button';

const EditCategoryPage: FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const categoryId = useSearchQuery('category') as string;
  const { category, loading: isFetching } = useCategory(categoryId);

  const editCategorySubmitHandler = async (values: any) => {
    setLoading(true);
    try {
      const data = {
        name: values.title,
        description: values.description,
        country: values.country,
        categoryImage: values.imageFile,
        value: values.title,
      };
      const res = await DAV_APIS.country.editCategory(data, categoryId);
      setLoading(false);
      if (res.status === 200) {
        Alert({ variant: 'success', message: 'Category updated successfully' });
        navigate('/dashboard/categories');
      }
    } catch (error) {
      setLoading(false);
      Alert({ variant: 'error', message: 'Failed to update category, Please try again' });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className='w-full space-y-6 pt-4 pb-10'>
      <div className='flex items-center'>
        <button
          onClick={() => navigate('/dashboard/categories')}
          type='button'
          className='flex items-center gap-2 text-gray-500 hover:text-primary-600 transition-colors font-semibold'
        >
          <MdArrowBackIosNew className='w-4 h-4' />
          <span>Back to Categories</span>
        </button>
      </div>

      <div className='bg-white dark:bg-gray-800 rounded-2xl p-8 border border-gray-100 dark:border-gray-700 shadow-sm min-h-[400px]'>
        <h1 className='text-3xl font-extrabold text-gray-900 dark:text-white mb-8 text-center tracking-tight'>
          Edit Category
        </h1>

        {isFetching ? (
          <div className='py-20 flex justify-center'>
            <DashboardLoader />
          </div>
        ) : (
          <Formik
            enableReinitialize
            initialValues={{
              title: category.name || '',
              description: category.description || '',
              imageCover: category.tourCategoryImage || '',
              imageFile: '',
              country: category?.country?.id || '',
            }}
            validationSchema={categorySchema}
            onSubmit={async (values, { resetForm }) => {
              await editCategorySubmitHandler(values);
              resetForm();
            }}
          >
            {({ handleSubmit, values, setFieldValue, dirty, isValid }) => (
              <form onSubmit={handleSubmit} className='w-full'>
                <CategoryForm values={values} setFieldValue={setFieldValue} />
                <div className='flex justify-center mt-10 border-t border-gray-100 dark:border-gray-700 pt-8'>
                  <Button
                    type='submit'
                    variant='primary'
                    loading={loading}
                    disabled={!dirty || !isValid}
                    className='w-full sm:w-64'
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </Formik>
        )}
      </div>
      <SEO
        isEdit
        pageType='category'
        initialData={{
          title: category.name,
          description: category.description,
          image: category.tourCategoryImage,
          slug: category.slug,
        }}
      />
    </div>
  );
};

export default EditCategoryPage;
